import { NativeClientService } from '@shared/services/native-client.service';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import {
  isUserRoleAllowedForApp,
  userRoleNewSelector,
} from '../store/selectors/user-role.selectors';
import { twoFactorAuthCompletedGuard } from './two-factor-auth-completed.guard';
import { AuthService } from '../services/auth.service';
import { fullAuthenticated } from '../store/actions/session-auth.actions';
import { verifiedUserRoles } from '../store/reducers/session-info.reducer';
import { serialCanActivate } from '@xt/core/utils';

/**
 * Guard to verify user roles and platform-specific access after 2FA check.
 * It performs the following checks:
 * 1. Checks if the user has verified roles. If not, redirects to the identification flow.
 * 2. For web users, enforces 2FA via `authService.enforce2FA()`.
 * 3. For native app users, checks if their role is permitted to use the app.
 */
const userVerificationGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const store = inject(Store);
  const nativeClientService = inject(NativeClientService);
  const authService = inject(AuthService);
  const router = inject(Router);

  return store.pipe(
    select(userRoleNewSelector),
    take(1),
    switchMap((roles) => {
      const hasVerifiedRole = roles.some((item) =>
        verifiedUserRoles.includes(item),
      );

      if (!hasVerifiedRole) {
        // User not verified, redirect to identification
        const redirectPath = nativeClientService.isNative
          ? '/start-verification-app'
          : '/identification';
        return of(router.parseUrl(redirectPath));
      }

      // User is verified, proceed with platform-specific checks
      if (!nativeClientService.isNative) {
        return authService.enforce2FA$(state.url).pipe(
          map(() => {
            store.dispatch(fullAuthenticated());
            return true;
          }),
          catchError(() => {
            authService.connectOAuth(false, state.url);
            return of(false);
          }),
        );
      } else {
        // Native: Check if user role is allowed for the app
        return store.pipe(
          select(isUserRoleAllowedForApp),
          take(1),
          map((isAllowedToUseApp) => {
            if (isAllowedToUseApp) {
              store.dispatch(fullAuthenticated());
              return true;
            } else {
              return router.parseUrl('/no-app-access');
            }
          }),
        );
      }
    }),
  );
};

/**
 * A composite guard that ensures a user is fully authenticated and authorized.
 * It runs the following guards in sequence:
 * 1. `twoFactorAuthCompletedGuard`: Checks for basic authentication and 2FA completion.
 * 2. `userVerificationGuard`: Verifies user roles and platform-specific access rights.
 */
export const authFullUserGuard = serialCanActivate(
  twoFactorAuthCompletedGuard,
  userVerificationGuard,
);
